import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Paper,
  Grid
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';
import config from '../config';

function OrdenDetalleForm() {
  const { id } = useParams();
  const { idDetalle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();

  const [detalle, setDetalle] = useState({
    id_orden_servicio: id,
    id_servicio: '',
    cantidad: 1,
    precio: '',
    detalle_glosa: '',
  });

  const [servicios, setServicios] = useState([]);
  const [orden, setOrden] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Cargar detalles de la orden
        const ordenRes = await axios.get(`${config.API_BASE_URL}/api/ordenes/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        setOrden(ordenRes.data);

        // Si la orden está en un estado que no permite edición, mostrar error
        if (['5', '6', '7'].includes(ordenRes.data.id_estado_servicio)) {
          setError('Esta orden no puede ser modificada en su estado actual');
          return;
        }

        // Cargar servicios disponibles
        const serviciosRes = await axios.get(`${config.API_BASE_URL}/api/servicios`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        setServicios(serviciosRes.data);

        // Si estamos editando, cargar el detalle existente
        if (idDetalle) {
          const detalleRes = await axios.get(
            `${config.API_BASE_URL}/api/detalle-ordenes/${id}/${idDetalle}`,
            { headers: { Authorization: `Bearer ${user.token}` } }
          );
          setDetalle(detalleRes.data);
        }
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setError('Error al cargar los datos necesarios');
      }
    };

    loadInitialData();
  }, [id, idDetalle]);

  const handleServiceChange = (e) => {
    const servicioSeleccionado = servicios.find(s => s.id_servicio === e.target.value);
    setDetalle(prev => ({
      ...prev,
      id_servicio: e.target.value,
      precio: servicioSeleccionado?.precio || 0
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDetalle(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (error) return;

    try {
      if (idDetalle) {
        await axios.put(
          `${config.API_BASE_URL}/api/detalle-ordenes/${idDetalle}`,
          detalle,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      } else {
        await axios.post(
          `${config.API_BASE_URL}/api/detalle-ordenes`,
          detalle,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }
      navigate(`/home/ordenes/editar/${id}`);
    } catch (error) {
      setError('Error al guardar el detalle del servicio');
    }
  };

  if (error) {
    return (
      <Container maxWidth="sm">
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        <Button
          variant="contained"
          onClick={() => navigate(`/home/ordenes/editar/${id}`)}
          sx={{ mt: 2 }}
        >
          Volver a la Orden
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          {idDetalle ? 'Editar Servicio' : 'Agregar Servicio'}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1">
            <strong>Orden:</strong> #{id}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Estado:</strong> {orden?.nombre_estado_servicio || 'Cargando...'}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Servicio</InputLabel>
                <Select
                  name="id_servicio"
                  value={detalle.id_servicio}
                  onChange={handleServiceChange}
                  required
                >
                  {servicios.map((servicio) => (
                    <MenuItem key={servicio.id_servicio} value={servicio.id_servicio}>
                      {servicio.nombre_servicio} - {simboloMoneda} {servicio.precio}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Cantidad"
                type="number"
                fullWidth
                name="cantidad"
                value={detalle.cantidad}
                onChange={handleChange}
                required
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Precio"
                type="number"
                fullWidth
                name="precio"
                value={detalle.precio}
                onChange={handleChange}
                required
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Observaciones"
                multiline
                rows={4}
                fullWidth
                name="detalle_glosa"
                value={detalle.detalle_glosa}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/home/ordenes/editar/${id}`)}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={!!error}
                >
                  {idDetalle ? 'Actualizar Servicio' : 'Agregar Servicio'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
}

export default OrdenDetalleForm;